import React, { useState } from 'react';
import IdentificationPartialForm from './PartialForm/IdentificationPartialForm';
import {
    checkIfKnownIbanBceCombination,
    formatNumberAddress,
    formatNumberCompany,
    formatPhone,
    handleDeleteFile,
    handleFileValidation,
    isValidNationalRegisterNumber,
} from './FormHelper';
import AddressPartialForm from './PartialForm/AddressPartialForm';
import ContactPartialForm from './PartialForm/ContactPartialForm';
import SectorPartialForm from './PartialForm/SectorPartialForm';
import { IFPIEFormData } from './Types/IFPIEFormData';
import { IbanOwnerType } from './Types/IbanOwnerType';
import { useTranslation } from '../../Helpers';
import { WidgetHelper } from '../WidgetHelper';
import { FloatLabel } from '../../Components';
import { validateFPIEForm } from './FormValidator';
import { PrimeFpieService } from '../../Services';
import { PrimeSuccessWidget } from './PrimeSuccessWidget';

const PrimeFpieForm = ({
    language,
    translationJson,
}: {
    language: string;
    translationJson: string;
}) => {
    const [data, setData] = useState<IFPIEFormData>({
        name: '',
        surname: '',
        denominationCompany: '',
        numberCompany: '',
        socialCompany: '',
        addressStreet: '',
        addressNumber: '',
        addressPostal: '',
        addressLocality: '',
        mail: '',
        mailConfirmation: '',
        phone: '',
        phoneConfirmation: '',
        iban: '',
        isIbanOk: false,
        bic: '',
        isKnownCombinationCompanyIBAN: false,
        ibanOwner: IbanOwnerType.OwnerAsker,
        sectorCompany: '',
        workCompany: '',
        commissionCompany: '',
        nationalRegisterTrainee: '',
        nationalRegisterTraineeIsValid: false,
        bankAttestationFile: null as any,
        dataAreCorrect: false,
    });

    const [errors, setErrors] = useState<
        Partial<Record<keyof IFPIEFormData, string>>
    >({});
    const [isSubmit, setIsSubmit] = useState<boolean>(false);
    const [isSuccess, setIsSuccess] = useState<boolean>(false);

    const translations = useTranslation(translationJson) as Record<
        string,
        string
    >;

    const borderRadius = {
        borderRadius: '5px',
    } as React.CSSProperties;

    const handleChange = <K extends keyof IFPIEFormData>(
        field: K,
        value: IFPIEFormData[K]
    ) => {
        setData((prev) => ({ ...prev, [field]: value }));
    };

    const handleChangeNumberCompany = (value: string) => {
        const formatted = formatNumberCompany(value);
        setData((prev) => ({ ...prev, numberCompany: formatted }));
    };

    const handleBlurNumberCompany = async () => {
        if (data.iban && data.numberCompany && data.isIbanOk) {
            const isKnown = await checkIfKnownIbanBceCombination(
                data.iban,
                data.numberCompany
            );
            setData((prev) => ({
                ...prev,
                isKnownCombinationCompanyIBAN: isKnown,
            }));
        } else {
            setData((prev) => ({
                ...prev,
                isKnownCombinationCompanyIBAN: false,
            }));
        }
    };

    const handleBlurNumberCompanyVoid = () => {
        void handleBlurNumberCompany();
    };

    const handleChangeNumberAddress = (value: string) => {
        const formatted = formatNumberAddress(value);
        setData((prev) => ({ ...prev, addressNumber: formatted }));
    };

    const handleChangePhone = (value: string, field: string) => {
        const phoneFormatted = formatPhone(value);
        setData((prev) => ({ ...prev, [field]: phoneFormatted }));
    };

    const handleSelectSector = (event: { sector: string; work: string }) => {
        setData((prev) => ({
            ...prev,
            sectorCompany: event.sector,
            workCompany: event.work,
        }));
    };

    const handleChangeNationalRegisterNumber = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const input = event.target;

        const match = input.value
            .replace(/\D/g, '')
            .match(/(\d{1,2})(\d{1,2})?(\d{1,2})?(\d{1,3})?(\d{1,2})?/);

        if (match) {
            const [, a, b, c, d, e] = match;

            input.value =
                `${a}` +
                (b ? `.${b}` : '') +
                (c ? `.${c}` : '') +
                (d ? `-${d}` : '') +
                (e ? `.${e}` : '');
        }

        const cleanValue = match ? match.slice(1).join('') : '';
        const isValid = isValidNationalRegisterNumber(cleanValue);

        setData((prev) => ({
            ...prev,
            nationalRegisterTrainee: input.value,
            nationalRegisterTraineeIsValid: isValid,
        }));
    };

    const handleChangeCommission = (value: string) => {
        const digits = value.replace(/\D/g, '');
        const prefix = translations['Site.Prime.CommissionValue'] + ' ';

        const formatted =
            digits.length === 0
                ? ''
                : digits.length < 4
                  ? prefix + digits.padEnd(3, '0').substring(0, 3)
                  : prefix +
                    `${digits.substring(0, 3)}.${digits.substring(3, 5)}`;

        setData((prev) => ({ ...prev, commissionCompany: formatted }));
    };

    const handleChangeFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            handleFileValidation<IFPIEFormData>(e.target.files[0], {
                fieldName: 'bankAttestationFile',
                setData,
            });
        }
    };

    const handleHiddenBankAttestationButton = (e?: React.MouseEvent) => {
        e?.preventDefault();
        const button = document.getElementById(
            'bankAttestationFileButton'
        ) as HTMLInputElement | null;
        button?.click();
    };

    const onFileDelete = (e?: React.MouseEvent) => {
        e?.preventDefault();
        handleDeleteFile<IFPIEFormData>('bankAttestationFile', setData);
    };

    const handleSubmit = () => {
        void handleSubmitAsync();
    };

    const handleSubmitAsync = async () => {
        const validationErrors = validateFPIEForm(data, translations);
        setErrors(validationErrors);

        if (Object.keys(validationErrors).length === 0) {
            try {
                if (typeof window.getCaptchaToken !== 'function') {
                    throw new Error('Captcha is not available');
                }

                const captchaToken =
                    await window.getCaptchaToken('prime_fpie_request');

                if (!captchaToken) {
                    throw new Error('Captcha token is empty');
                }

                const response = await PrimeFpieService.pushData(
                    data,
                    captchaToken
                );

                if (
                    response &&
                    response.status === 200 &&
                    (await response.json()) === true
                ) {
                    setIsSubmit(true);
                    setIsSuccess(true);
                } else {
                    setIsSubmit(true);
                    setIsSuccess(false);
                }
            } catch (error) {
                console.error('Erreur lors de la soumission:', error);
                setIsSubmit(true);
                setIsSuccess(false);
            }
        }
    };

    if (isSubmit) {
        return (
            <PrimeSuccessWidget
                isSuccess={isSuccess}
                dictSuccessText1={translations['Site.Prime.SuccessTextFpie1']}
                dictSuccessText2=""
                dictSuccessContact1={
                    translations['Site.Prime.SuccessContactEmployeur']
                }
                dictSuccessContact2=""
                dictFailLabel={''}
                dictSuccessPhone={''}
                dictSuccessSignature1={''}
                dictSuccessSignature2={''}
            />
        );
    }

    return (
        <form
            id="primeForm"
            data-skip-captcha="true"
            onSubmit={(e) => {
                e.preventDefault();
                handleSubmit();
            }}
        >
            <div className="row">
                <div className="col-12">
                    <h2 className="h2">
                        {translations['Site.Prime.TitleCompany']}
                    </h2>
                </div>
            </div>

            <IdentificationPartialForm
                values={data}
                onChangeNumberCompany={handleChangeNumberCompany}
                onChange={handleChange}
                errors={errors}
                dictNameLabel={translations['Site.Prime.NameCompany']}
                dictSurnameLabel={translations['Site.Prime.SurnameCompany']}
                dictDenominationCompanyLabel={
                    translations['Site.Prime.DenominationCompany']
                }
                dictNumberCompanyLabel={
                    translations['Site.Prime.NumberCompany']
                }
                dictSocialCompanyLabel={
                    translations['Site.Prime.SocialCompany']
                }
                dictErrorSubmit={translations['Site.Prime.ErrorSubmit']}
                dictIdentificationCodeError={
                    translations['Site.Prime.IdentificationCodeError']
                }
            />

            <div className="row">
                <div className="col-12">
                    <h2 className="h2">
                        {translations['Site.Prime.AddressCompany']}
                    </h2>
                </div>
            </div>

            <AddressPartialForm
                values={data}
                onChange={handleChange}
                onChangeNumberAddress={handleChangeNumberAddress}
                errors={errors}
                dictAddressStreetLabel={
                    translations['Site.Prime.AddressStreet']
                }
                dictAddressNumberLabel={
                    translations['Site.Prime.AddressNumber']
                }
                dictAddressPostalLabel={
                    translations['Site.Prime.AddressPostal']
                }
                dictAddressLocalityLabel={
                    translations['Site.Prime.AddressLocality']
                }
                dictErrorSubmit={translations['Site.Prime.ErrorSubmit']}
            />

            <ContactPartialForm
                values={data}
                onChange={handleChange}
                onChangePhone={handleChangePhone}
                onIbanCheck={handleBlurNumberCompanyVoid}
                errors={errors}
                dictMailLabel={translations['Site.Prime.MailCompany']}
                dictMailConfirmationLabel={
                    translations['Site.Prime.MailCompanyConfirmation']
                }
                dictPhoneLabel={translations['Site.Prime.PhoneCompany']}
                dictPhoneConfirmationLabel={
                    translations['Site.Prime.PhoneConfirmation']
                }
                dictIbanCompanyLabel={translations['Site.Prime.IbanCompany']}
                dictBicCompanyLabel={translations['Site.Prime.BicCompany']}
                dictErrorSubmit={translations['Site.Prime.ErrorSubmit']}
                dictMailConfirmationError={
                    translations['Site.Prime.MailCompanyConfirmationError']
                }
                dictPhoneConfirmationError={
                    translations['Site.Prime.PhoneConfirmationError']
                }
                dictIdentificationCodeError={
                    translations['Site.Prime.IdentificationCodeError']
                }
                isIbanOk={data.iban.length >= 16}
                isBelgianIban={data.iban.startsWith('BE')}
            />

            <SectorPartialForm
                language={language}
                dictErrorSubmit={translations['Site.Prime.ErrorSubmit']}
                dictCommissionCompanyLabel={
                    translations['Site.Prime.CommissionCompany']
                }
                dictIdentificationCodeError={
                    translations['Site.Prime.IdentificationCodeError']
                }
                values={data}
                errors={errors}
                onSelectSector={handleSelectSector}
                onChangeCommission={handleChangeCommission}
                onKeyDownNumber={(e) => {
                    if (
                        !/[0-9]/.test(e.key) &&
                        e.key !== 'Backspace' &&
                        e.key !== 'Tab'
                    ) {
                        e.preventDefault();
                    }
                }}
            />

            <div className="row">
                <div className="col-12">
                    <h2 className="h2">
                        {translations['Site.Prime.TitlePrimeInformation']}
                    </h2>
                </div>
            </div>

            <FloatLabel
                active={!!data.nationalRegisterTrainee}
                label={translations['Site.Prime.NationalRegisterTrainee']}
                labelId={'nationalRegisterTrainee'}
            >
                <input
                    type="text"
                    name="nationalRegisterTrainee"
                    value={data.nationalRegisterTrainee}
                    onChange={handleChangeNationalRegisterNumber}
                />
            </FloatLabel>

            {errors.nationalRegisterTrainee && (
                <div className="row">
                    <label className="alert-danger m-auto">
                        {translations['Site.Prime.ErrorSubmit']}
                    </label>
                </div>
            )}
            {errors.nationalRegisterTraineeIsValid && (
                <div className="row">
                    <label className="alert-danger m-auto">
                        {translations['Site.Prime.IdentificationCodeError']}
                    </label>
                </div>
            )}

            <div className="col-12">
                <h3 className="h3">{translations['Site.Prime.TitleFiles']}</h3>
                <div className="row px-4">
                    <div className={'col-12 pb-4 border'} style={borderRadius}>
                        <div className="row">
                            <div
                                className="col-6 text-left border-right py-4 pl-4"
                                style={{ lineHeight: '100%' }}
                            >
                                {translations['Site.Prime.BankAttestationFile']}
                                <br />
                                <span
                                    className="font-weight-light"
                                    style={{ fontSize: '12px' }}
                                >
                                    {
                                        translations[
                                            'Site.Prime.BankAttestationFileInfo'
                                        ]
                                    }
                                </span>
                            </div>

                            {!data.bankAttestationFile && (
                                <div className="col-6 text-right pt-4 pr-4 alert-light">
                                    {translations['Site.Prime.NoneLabel']}
                                    <input
                                        type="file"
                                        name="bankAttestationFile"
                                        id="bankAttestationFileButton"
                                        onChange={handleChangeFiles}
                                        hidden
                                    />
                                    <button
                                        className="ml-3"
                                        type="button"
                                        onClick={
                                            handleHiddenBankAttestationButton
                                        }
                                    >
                                        {translations['Site.Prime.Upload']}
                                    </button>
                                    {errors.bankAttestationFile && (
                                        <div className="row">
                                            <label className="alert-danger m-auto">
                                                {
                                                    translations[
                                                        'Site.Prime.ErrorSubmit'
                                                    ]
                                                }
                                            </label>
                                        </div>
                                    )}
                                </div>
                            )}

                            {!!data.bankAttestationFile && (
                                <div className="col-6 text-right pt-4 pr-0">
                                    <div className="row pr-0">
                                        <div className="col-8">
                                            {data.bankAttestationFile.name}
                                        </div>
                                        <div className="col-4 mt-n3">
                                            <button
                                                type="button"
                                                onClick={onFileDelete}
                                                className="btn-no-look"
                                            >
                                                <i className="icon-cross"></i>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="col-12 text-left pt-3" style={borderRadius}>
                <input
                    type="checkbox"
                    id="dataAreCorrect"
                    name="dataAreCorrect"
                    onChange={(e) =>
                        handleChange('dataAreCorrect', e.target.checked)
                    }
                />
                <label htmlFor="dataAreCorrect">
                    {translations['Site.Prime.DataAreCorrectLabel']}
                </label>
                {errors.dataAreCorrect && (
                    <div className="row">
                        <label className="alert-danger m-auto">
                            {translations['Site.Prime.ErrorSubmit']}
                        </label>
                    </div>
                )}
            </div>

            <div className="col-12 text-right pt-3">
                <label className="alert-light">
                    {translations['Site.PrimePhoenix.MandatoryField']}
                </label>
            </div>

            <div className="form-group submit mt-5">
                <button type="submit" className="btn-block">
                    <i className="icon-check"></i>
                    {translations['Site.FondsRebond.Submit']}
                </button>
            </div>
        </form>
    );
};

WidgetHelper.register(PrimeFpieForm, 'prime-fpie-form-widget');
